import { useEffect, useRef } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from '../icons'
import { formatTRY } from '../../utils/money'
import type { PublicCampaign, PublicProduct } from '../../services/publicMenu'
import { STRINGS, type MenuLang } from './menuI18n'
import './CampaignPopup.css'

/** Pencerede bir kampanya: kendisi, fotoğrafı ve kapsadığı ürünler. */
export interface CampaignCard {
  campaign: PublicCampaign
  image: string
  /** Kapsanan ürünler, seçilen dilde; bütün menüdeyse boş. */
  products: (PublicProduct & { dealKurus?: number })[]
  wholeMenu: boolean
}

/** Büyük rozet: "%20", "50 ₺", "3 al 2 öde". */
export function dealBadge(c: PublicCampaign, lang: MenuLang): string {
  const t = STRINGS[lang]
  if (c.kind === 'percent') return lang === 'tr' ? `%${c.percent ?? 0}` : `${c.percent ?? 0}%`
  if (c.kind === 'amount') return formatTRY(c.amountKurus ?? 0)
  return t.buyPay(c.buyQty ?? 0, c.payQty ?? 0)
}

/** Yüzde indirimde (en az tutar yoksa) ürünün indirimli fiyatı; yoksa undefined. */
export function dealPrice(c: PublicCampaign, priceKurus: number): number | undefined {
  if (c.kind !== 'percent' || !c.percent || c.minTotalKurus) return undefined
  return Math.round((priceKurus * (100 - c.percent)) / 100)
}

function ruleLine(c: PublicCampaign, lang: MenuLang): string {
  const t = STRINGS[lang]
  const min = c.minTotalKurus ? `, ${t.over(formatTRY(c.minTotalKurus))}` : ''
  if (c.kind === 'bogo') return t.buyPay(c.buyQty ?? 0, c.payQty ?? 0)
  return `${dealBadge(c, lang)} ${t.off}${min}`
}

/** "Pzt, Sal · 14.00-17.00 · 31 Eki tarihine kadar", müşterinin dilinde. */
function whenLine(c: PublicCampaign, lang: MenuLang): string {
  const t = STRINGS[lang]
  const locale = lang === 'tr' ? 'tr-TR' : lang
  const parts: string[] = []
  if (c.days.length > 0 && c.days.length < 7) {
    // 2026-10-04 bir Pazar: getDay() değeriyle aynı sıradan gün adı.
    const name = (d: number) =>
      new Date(2026, 9, 4 + d).toLocaleDateString(locale, { weekday: 'short' })
    parts.push([...c.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map(name).join(', '))
  }
  if (c.startTime && c.endTime && c.startTime !== c.endTime) {
    parts.push(`${c.startTime.replace(':', '.')}-${c.endTime.replace(':', '.')}`)
  }
  if (c.endsOn) {
    const d = new Date(`${c.endsOn}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short' })
    parts.push(t.until(d))
  }
  return parts.length ? parts.join(', ') : t.daily
}

const SHOWN = 4

/**
 * Karekodu okutan müşteriye o an geçerli kampanyalar (16 Eki 2026).
 *
 * Her açılışta çıkıyor (kullanıcı kararı). Birden çok kampanya yan yana
 * kaydırılıyor. Ürüne dokununca pencere kapanıp ürünün kendi penceresi
 * açılıyor.
 */
export function CampaignPopup({
  cards,
  lang,
  onClose,
  onPick,
}: {
  cards: CampaignCard[]
  lang: MenuLang
  onClose: () => void
  onPick: (p: PublicProduct) => void
}) {
  const t = STRINGS[lang]
  const track = useRef<HTMLDivElement>(null)

  /** Kartlar arasında bir adım: resmin iki yanındaki düğmeler ve ok tuşları. */
  const go = (dir: -1 | 1) => {
    const el = track.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  return (
    <div className="pmc-bg" onClick={onClose}>
      <div
        className="pmc"
        role="dialog"
        aria-modal="true"
        aria-label={t.campaigns}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="pm__sheet-x pmc__x" onClick={onClose} aria-label="Kapat">
          <XIcon />
        </button>

        {/* Birden çok kampanyada kaydırılabildiği belli olsun (31 Eki 2026, kullanıcı:
            "alttaki noktayı kimse görmüyor"): açılınca kartlar bir kez yana kıpırdıyor,
            köşede 1/3. Noktalar ve oklar kalktı (kullanıcı); geçiş yalnız kaydırarak. */}
        <div ref={track} className={`pmc__track ${cards.length > 1 ? 'is-hint' : ''}`}>
          {cards.map(({ campaign: c, image, products, wholeMenu }, i) => (
            <article key={c.id} className="pmc__card" aria-roledescription={t.campaign}>
              <div className="pmc__hero">
                <img src={image} alt="" decoding="async" />
                {/* Geçiş düğmeleri resmin iki yanında (3 Kas 2026, kullanıcı):
                    masaüstünde kaydırmanın yolu yoktu. İlkte geri, sonda ileri yok. */}
                {i > 0 && (
                  <button type="button" className="pmc__nav pmc__nav--prev" onClick={() => go(-1)} aria-label={t.prev}>
                    <ChevronLeftIcon />
                  </button>
                )}
                {i < cards.length - 1 && (
                  <button type="button" className="pmc__nav pmc__nav--next" onClick={() => go(1)} aria-label={t.next}>
                    <ChevronRightIcon />
                  </button>
                )}
                <span className="pmc__kicker">
                  {t.campaign}
                  {cards.length > 1 && <span className="pmc__count">{`${i + 1}/${cards.length}`}</span>}
                </span>
                {c.kind === 'bogo' ? (
                  <span className="pmc__badge">{dealBadge(c, lang)}</span>
                ) : (
                  // Yüzde ve tutar: yıldız kenarlı indirim etiketi (kullanıcı,
                  // 16 Eki 2026: "%20 daha cazip görünsün").
                  <span className="pmc__burst" aria-label={ruleLine(c, lang)}>
                    <span className="pmc__burst-in">
                      {c.kind === 'percent' ? (
                        <b>
                          {lang === 'tr' && <i>%</i>}
                          {c.percent ?? 0}
                          {lang !== 'tr' && <i>%</i>}
                        </b>
                      ) : (
                        <b className="pmc__burst-amount">{formatTRY(c.amountKurus ?? 0).replace(/,00(?=\D*$)/, '')}</b>
                      )}
                      <small>{t.off}</small>
                    </span>
                  </span>
                )}
              </div>
              <div className="pmc__body">
                <h2>{c.name}</h2>
                {/* Rozet kuralı söylüyor; burada yalnız ek koşul (en az tutar). */}
                {c.minTotalKurus ? <p className="pmc__rule">{ruleLine(c, lang)}</p> : null}
                <p className="pmc__when">{whenLine(c, lang)}</p>

                {wholeMenu ? (
                  <p className="pmc__all">{t.wholeMenu}</p>
                ) : (
                  <ul className="pmc__items">
                    {products.slice(0, SHOWN).map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => onPick(p)}>
                          {p.image ? (
                            <img className="pmc__thumb" src={p.image} alt="" loading="lazy" />
                          ) : (
                            <span className="pmc__thumb pmc__thumb--none" aria-hidden="true">
                              {p.name.slice(0, 1)}
                            </span>
                          )}
                          <span className="pmc__name">{p.name}</span>
                          <span className="pmc__price">
                            {p.dealKurus !== undefined && <s>{formatTRY(p.priceKurus)}</s>}
                            <b>{formatTRY(p.dealKurus ?? p.priceKurus)}</b>
                            {p.byWeight && <small> / kg</small>}
                          </span>
                        </button>
                      </li>
                    ))}
                    {products.length > SHOWN && <li className="pmc__more">{t.more(products.length - SHOWN)}</li>}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>

        <button type="button" className="pmc__go" onClick={onClose}>
          {t.toMenu}
        </button>
      </div>
    </div>
  )
}
